import React, { useCallback, useEffect } from "react";
import cn from "classnames";
import { createPortal } from "react-dom";
import { disableBodyScroll, clearAllBodyScrollLocks } from "body-scroll-lock";
import styles from "./ModalProduct.module.sass";
import Icon from "../Icon";

const ModalConditionGeneral = ({ visible, onClose, title, figcaption, gallery, isFirstConnexion = false }) => {
  const escFunction = useCallback(
    (e) => {
      if (e.keyCode === 27 && !isFirstConnexion) {
        onClose();
      }
    },
    [onClose, isFirstConnexion]
  );

  useEffect(() => {
    document.addEventListener("keydown", escFunction, false);
    return () => {
      document.removeEventListener("keydown", escFunction, false);
    };
  }, [escFunction]);

  useEffect(() => {
    if (visible) {
      const target = document.querySelector("#modal-product");
      disableBodyScroll(target);
    } else {
      clearAllBodyScrollLocks();
    }
  }, [visible]);

  return createPortal(
    visible && (
      <div id="modal-product" className={styles.modal} onClick={isFirstConnexion ? null : undefined}>
        <div className={styles.control}>
          <button className={styles.close} onClick={onClose}>
            <Icon name="close" size="20" />
          </button>
        </div>
        <div className={styles.outer}>
          <div className={styles.overview}>
            <div className={cn("title-green", styles.subtitle)}>Conditions Générales d'Utilisation</div>
            <div className={styles.content}>
              <section id="mentions-legales" className={styles.section}>
                <h3 className={styles.sectionTitle}>1. Mentions légales</h3>
                <p>
                  La consultation de la plateforme Edutio ainsi que son utilisation sont subordonnées à l'acceptation 
                  de nos Conditions Générales d'Utilisation.
                </p>
                <p>
                  La plateforme Edutio est la propriété de VAISSEAU PAUL, Entrepreneur Individuel, 
                  SIREN : 980 721 039, SIRET : 980 721 039 00017.
                </p>
                <p>
                  Numéro de TVA : FR63980721039<br/>
                  Email : contact@edutio.fr<br/>
                  Directeur de la publication : VAISSEAU PAUL
                </p>
                <p>
                  La plateforme et ses données sont conservées et stockées sur des serveurs sécurisés pendant toute 
                  la durée des traitements.
                </p>
              </section>

              <section id="acces-plateforme" className={styles.section}>
                <h3 className={styles.sectionTitle}>2. Accès à la plateforme</h3>
                <p>
                  L'utilisateur accède aux services Edutio en s'inscrivant sur la plateforme. La plateforme est destinée 
                  aux établissements de formation, CFA, organismes de formation et leurs collaborateurs.
                </p>
                <p>
                  Les utilisateurs déclarent être juridiquement capables de souscrire aux services proposés par Edutio 
                  et disposer des autorisations nécessaires pour représenter leur établissement.
                </p>
                <p>
                  L'accès à la plateforme peut être restreint à l'égard de certaines personnes ou pays conformément 
                  à la législation spécifique du pays en question.
                </p>
                <p>
                  Les identifiants de connexion sont strictement personnels à l'utilisateur. Ils doivent correspondre 
                  à des critères de sécurité déterminés par Edutio et ne doivent jamais être communiqués à des tiers.
                </p>
                <p>
                  L'utilisateur s'engage à fournir des informations exactes, complètes, à jour et sincères tout au long 
                  de sa relation avec Edutio. En cas de changement de situation, l'utilisateur s'engage à modifier 
                  les informations sur son compte.
                </p>
              </section>

              <section id="services-proposes" className={styles.section}>
                <h3 className={styles.sectionTitle}>3. Services proposés</h3>
                <p>
                  Edutio propose une solution complète de gestion pour les centres de formation d'apprentis (CFA) 
                  et organismes de formation, incluant notamment :
                </p>
                <ul>
                  <li>Gestion des apprenants et de leur parcours de formation</li>
                  <li>Suivi de l'émargement et de l'assiduité</li>
                  <li>Gestion administrative et documentaire</li>
                  <li>Outils de communication et de collaboration</li>
                  <li>Rapports et statistiques de suivi</li>
                  <li>Interface avec les systèmes d'information existants</li>
                </ul>
                <p>
                  Edutio se réserve le droit de faire évoluer, modifier ou suspendre, sans préavis, la plateforme 
                  pour des raisons de maintenance ou pour tout autre motif jugé nécessaire.
                </p>
              </section>

              <section id="collecte-donnees" className={styles.section}>
                <h3 className={styles.sectionTitle}>4. Collecte des données</h3>
                <p>
                  La plateforme assure à l'utilisateur une collecte et un traitement d'informations personnelles 
                  dans le respect de la vie privée conformément au Règlement Général sur la Protection des Données (RGPD) 
                  et à la loi n°78-17 du 6 janvier 1978 relative à l'informatique, aux fichiers et aux libertés.
                </p>
                <p>
                  En vertu de la loi Informatique et Libertés et du RGPD, l'utilisateur dispose d'un droit d'accès, 
                  de rectification, de suppression, d'opposition, de limitation du traitement et de portabilité 
                  de ses données personnelles.
                </p>
                <p>L'utilisateur peut exercer ces droits :</p>
                <ul>
                  <li>Par email à l'adresse : contact@edutio.fr</li>
                  <li>Par voie postale à l'adresse du siège social</li>
                </ul>
                <p>
                  Les données collectées sont nécessaires au fonctionnement de la plateforme et à la fourniture 
                  des services. Elles ne sont pas transmises à des tiers sans consentement explicite, 
                  sauf obligation légale.
                </p>
              </section>

              <section id="propriete-intellectuelle" className={styles.section}>
                <h3 className={styles.sectionTitle}>5. Propriété intellectuelle</h3>
                <p>
                  Les marques, logos, signes ainsi que tous les contenus de la plateforme Edutio (textes, images, 
                  vidéos, sons, logiciels...) font l'objet d'une protection par le Code de la propriété intellectuelle 
                  et plus particulièrement par le droit d'auteur.
                </p>
                <p>
                  La marque Edutio et tous les éléments distinctifs de la plateforme sont des marques déposées. 
                  Toute représentation et/ou reproduction et/ou exploitation partielle ou totale de ces marques, 
                  de quelque nature que ce soit, est totalement prohibée sans autorisation écrite préalable.
                </p>
                <p>
                  L'utilisateur doit solliciter l'autorisation préalable d'Edutio pour toute reproduction, publication, 
                  copie des différents contenus. Il s'engage à une utilisation des contenus de la plateforme dans 
                  le cadre strict de l'utilisation des services, toute utilisation à des fins commerciales tierces 
                  est strictement interdite.
                </p>
              </section>

              <section id="responsabilite" className={styles.section}>
                <h3 className={styles.sectionTitle}>6. Responsabilité</h3>
                <p>
                  Les informations diffusées sur la plateforme Edutio sont réputées fiables mais Edutio ne garantit pas 
                  qu'elle soit exempte de défauts, d'erreurs ou d'omissions.
                </p>
                <p>
                  Malgré des mises à jour régulières, Edutio ne peut être tenu responsable de la modification 
                  des dispositions administratives et juridiques survenant après leur publication sur la plateforme.
                </p>
                <p>
                  Edutio ne peut être tenu pour responsable d'éventuels virus qui pourraient infecter l'ordinateur 
                  ou tout matériel informatique de l'utilisateur, suite à une utilisation, à l'accès, 
                  ou au téléchargement provenant de la plateforme.
                </p>
                <p>
                  La responsabilité d'Edutio ne peut être engagée en cas de force majeure ou du fait imprévisible 
                  et insurmontable d'un tiers.
                </p>
                <p>
                  L'utilisateur est seul responsable de l'utilisation qu'il fait de la plateforme et des données 
                  qu'il y saisit. Il s'engage à respecter les réglementations en vigueur, notamment en matière 
                  de protection des données personnelles.
                </p>
              </section>

              <section id="liens-hypertextes" className={styles.section}>
                <h3 className={styles.sectionTitle}>7. Liens hypertextes</h3>
                <p>
                  Des liens hypertextes peuvent être présents sur la plateforme Edutio. L'utilisateur est informé 
                  qu'en cliquant sur ces liens, il pourra sortir de la plateforme Edutio.
                </p>
                <p>
                  Edutio n'a pas de contrôle sur les pages web externes sur lesquelles aboutissent ces liens 
                  et ne saurait, en aucun cas, être responsable de leur contenu.
                </p>
              </section>

              <section id="cookies" className={styles.section}>
                <h3 className={styles.sectionTitle}>8. Cookies</h3>
                <p>
                  L'utilisateur est informé que lors de ses visites sur la plateforme, des cookies peuvent 
                  s'installer automatiquement sur son logiciel de navigation.
                </p>
                <p>
                  Les cookies sont de petits fichiers stockés temporairement sur le disque dur de l'ordinateur 
                  de l'utilisateur par le navigateur et qui sont nécessaires au bon fonctionnement de la plateforme.
                </p>
                <p>
                  Les cookies ne contiennent pas d'information personnelle permettant d'identifier directement 
                  l'utilisateur. Un cookie contient un identifiant unique, généré aléatoirement et donc anonyme.
                </p>
                <p>
                  L'information contenue dans les cookies est utilisée pour améliorer l'expérience utilisateur 
                  sur la plateforme Edutio. En naviguant sur la plateforme, l'utilisateur accepte l'utilisation 
                  des cookies nécessaires au fonctionnement.
                </p>
                <p>
                  L'utilisateur peut configurer son navigateur pour refuser les cookies, mais certaines 
                  fonctionnalités de la plateforme pourraient alors ne plus être disponibles.
                </p>
              </section>

              <section id="droit-applicable" className={styles.section}>
                <h3 className={styles.sectionTitle}>9. Droit applicable et juridiction compétente</h3>
                <p>
                  La législation française s'applique au présent contrat. En cas d'absence de résolution amiable 
                  d'un litige né entre les parties, les tribunaux français seront seuls compétents pour en connaître.
                </p>
                <p>
                  Pour toute question relative à l'application des présentes CGU, vous pouvez joindre Edutio 
                  aux coordonnées inscrites dans la section "Mentions légales".
                </p>
                <p>
                  Les présentes conditions générales d'utilisation peuvent être modifiées à tout moment. 
                  Les utilisateurs seront informés de toute modification par email ou notification sur la plateforme.
                </p>
                <p>
                  <strong>Date de dernière mise à jour :</strong> {new Date().toLocaleDateString('fr-FR')}
                </p>
              </section>
            </div>
          </div>
        </div>
      </div>
    ),
    document.body
  );
};

export default ModalConditionGeneral;
